/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useHeaderPanels } from '@/components/content/Header/context/HeaderPanelsContext';
import { HeaderCommonPanel } from '@/components/content/Header/parts/HeaderCommonPanel';
import { HeaderMiniCartDrawer } from '@/components/content/Header/parts/MiniCartDrawer';
import { HeaderMiniCartDropMenu } from '@/components/content/Header/parts/MiniCartDropMenu';
import {
	headerMiniCartIconLabelSX,
	headerMiniCartSX,
} from '@/components/content/Header/styles/miniCart';
import { headerMiniCartBadgeSX } from '@/components/content/Header/styles/miniCart/badge';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useLocalization } from '@/data/Localization';
import { useUser } from '@/data/User';
import { Badge, Box, Breakpoint, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePathname } from 'next/navigation';
import { isCart } from 'presentation/events/utils/ga4Utils';
import { FC, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

type Props = {
	mobileBreakpoint: Breakpoint;
};

export const HeaderMiniCart: FC<Props> = ({ mobileBreakpoint = 'sm' }) => {
	const idPopper = 'quickCartPanel';
	const { activePanelId, anchorEl, togglePanel, openPanel, closePanel } = useHeaderPanels();
	const { count, mutateCart } = useCart();
	const { message, clearMessage } = useNotifications();
	const { user: { userId } = {} } = useUser();
	const HeaderLabels = useLocalization('Header');
	const theme = useTheme();
	const pathname = usePathname();
	const initialized = useRef<boolean>(false);
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const miniCartEleRef = useRef<HTMLDivElement | null>(null);
	const router = useNextRouter();
	const [panelType, setPanelType] = useState<'panel' | 'drawer'>();

	const isPanelOpen = activePanelId === idPopper && panelType === 'panel';
	const isDrawerOpen = activePanelId === idPopper && panelType === 'drawer';
	const isMiniCartOpen = isPanelOpen || isDrawerOpen;
	const [isHovered, setIsHovered] = useState(false);
	const isActive = isMiniCartOpen || isHovered;

	const isCartPage = useMemo(() => (pathname ? isCart(pathname) : false), [pathname]);

	const handleClickOpenPanel: MouseEventHandler<HTMLDivElement> = useCallback(
		(event: React.MouseEvent<HTMLElement> | null) => {
			setPanelType(isMobile ? 'drawer' : 'panel');
			if (activePanelId === null) togglePanel(idPopper, event?.currentTarget ?? null);
		},
		[activePanelId, isMobile, togglePanel]
	);

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			togglePanel(idPopper, event?.currentTarget ?? null);
		}
	};

	const handleClose = () => {
		closePanel();
		setPanelType(undefined);
	};

	useEffect(() => {
		if (message.isAddToCart && !isCartPage) {
			setPanelType(isMobile ? 'drawer' : 'panel');
			openPanel(idPopper, !isMobile && miniCartEleRef?.current ? miniCartEleRef.current : null);
			clearMessage();
		}
	}, [message.isAddToCart, isCartPage, isMobile, openPanel, clearMessage]);

	useEffect(() => {
		if (!router.asPath) return;
		handleClose();
	}, [router.asPath]);

	useEffect(() => {
		if (initialized.current) {
			mutateCart();
		} else {
			initialized.current = true;
		}
	}, [mutateCart, userId]);

	return (
		<Box>
			<Stack
				aria-describedby="mini-cart-panel"
				aria-expanded={isMiniCartOpen}
				ref={miniCartEleRef}
				alignItems="center"
				onClick={handleClickOpenPanel}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				sx={headerMiniCartSX(isMiniCartOpen, theme)}
				tabIndex={0}
				onKeyDown={handleOnKeyDown}
				id="quickCartTrigger"
			>
				<Badge badgeContent={count} color="primary" sx={headerMiniCartBadgeSX}>
					<DynamicIcon
						name={MATERIAL_SYMBOL_ICONS.shoppingCart}
						variant={isActive ? 'filled' : 'rounded'}
					/>
				</Badge>
				<Typography sx={headerMiniCartIconLabelSX} variant="bodySubText">
					{HeaderLabels.miniCart.t()}
				</Typography>
			</Stack>

			<HeaderCommonPanel
				idPopper={idPopper}
				isOpen={isPanelOpen}
				anchorEl={anchorEl}
				handleClose={handleClose}
				needDrawer={false}
				closeAutomatically
			>
				<Stack direction="row">
					<HeaderMiniCartDropMenu />
				</Stack>
			</HeaderCommonPanel>
			<HeaderMiniCartDrawer idPaper={idPopper} isOpen={isDrawerOpen} onClose={handleClose} />
		</Box>
	);
};
